import React, { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, MapPin } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Textarea } from "../ui/Textarea";
import { FieldLabel } from "../ui/FieldLabel";
import { SearchableSelect } from "../ui/SearchableSelect";
import AddressPicker from "../addressPicker/AddressPicker";
import { isValidPostalCode } from "../postalCodeChipsInput";
import { getRegions } from "../../api/contracts";
import { updateDeliveryAddress } from "../../api/packages";
import { useCountry } from "../../context/CountryContext";
import {
  filterCommunesByCoverage,
  filterRegionsByCoverage,
  resolveCoverage,
} from "../../utils/coverageCommunes";
import { normalizeCommune } from "../../utils/communes";
import { geocodeDefaultsFor } from "../../utils/geocode";
import { showToast } from "../../utils/showToast";

// Topes del contrato de PATCH packages/:id/delivery-address.
const STREET_NAME_MAX = 200;
const STREET_NUMBER_MAX = 20;
const COMMENT_MAX = 500;
const ZIP_CODE_MAX = 12;

// Valor del desplegable para la región guardada en el pedido cuando el catálogo
// no la reconoce (otra grafía, otro país): se ofrece igual para poder guardar
// sin tocarla.
const CURRENT_REGION_VALUE = "__current_region__";

/**
 * Región del catálogo que corresponde a la del pedido. Primero por nombre; si
 * no coincide (se guardó con otra grafía), por la comuna, pero solo cuando una
 * única región la contiene: en Argentina los municipios se repiten entre
 * provincias y elegir uno al azar cambiaría la provincia sin que nadie lo note.
 */
function findCatalogRegion(regions, regionName, communeName) {
  const regionKey = normalizeCommune(regionName);
  const byName = regionKey ? regions.find((r) => normalizeCommune(r?.name) === regionKey) : null;
  if (byName) return byName;
  const communeKey = normalizeCommune(communeName);
  if (!communeKey) return null;
  const containing = regions.filter((r) =>
    (r?.communes ?? []).some((c) => normalizeCommune(c?.name) === communeKey),
  );
  return containing.length === 1 ? containing[0] : null;
}

// El valor vigente tiene que verse aunque la cobertura o el catálogo lo dejen
// fuera de la lista: si no, el campo aparecería vacío y guardar un cambio de
// calle obligaría a volver a elegir región y comuna.
const withOption = (options, option) =>
  !option || options.some((o) => o.value === option.value) ? options : [option, ...options];

// `null` y "" no son coordenadas: Number(null) da 0 y sembraría el pin en (0, 0).
const toPoint = (lat, lng) => {
  if (lat == null || lng == null || lat === "" || lng === "") return null;
  const point = { lat: Number(lat), lng: Number(lng) };
  return Number.isFinite(point.lat) && Number.isFinite(point.lng) ? point : null;
};

// Un opcional vacío viaja solo si el pedido tenía un valor: así borrarlo se
// guarda, y un campo que nunca tuvo valor no cuenta como cambiado.
const optionalField = (key, value, previous) => (value || previous ? { [key]: value } : {});

/**
 * Corrige la dirección de entrega de un pedido que todavía no salió a reparto.
 *
 * Se monta abierto y se desmonta al cerrar: cada apertura parte de la dirección
 * vigente del pedido, nunca de lo que quedó a medio escribir la vez anterior.
 *
 * Región y comuna salen del mismo catálogo que el alta a mano y se achican a la
 * cobertura del comercio (`resolveCoverage`); sin cobertura determinable (el
 * personal del operador no tiene, o el endpoint falló) se muestra el catálogo
 * completo, igual que el alta.
 *
 * El punto en el mapa es opcional. Sin confirmarlo, el backend conserva la
 * coordenada si no cambiaron calle, número ni comuna; si cambiaron, la descarta
 * y vuelve a geocodificar la dirección nueva.
 *
 * @param {object} props
 * @param {object} props.pkg Pedido con `_id`, `followingNumber` y `shipping_address`.
 * @param {() => void} props.onClose
 * @param {(updatedPackage: object) => void} props.onSaved La dirección cambió.
 * @param {() => void} props.onLocked El backend rechazó la edición porque el
 *   pedido ya cambió de estado: el contenedor debe recargarlo.
 */
export default function EditDeliveryAddressModal({ pkg, onClose, onSaved, onLocked }) {
  const { t } = useTranslation();
  const formId = useId();
  const {
    countryId,
    countryCode,
    labels,
    postalCodeRates,
    loading: countryLoading,
  } = useCountry();
  const postalEnabled = postalCodeRates?.enabled === true;
  const regionWord = labels?.region ?? t("Región");
  const communeWord = labels?.commune ?? t("Comuna");
  const geoDefaults = useMemo(() => geocodeDefaultsFor(countryCode), [countryCode]);

  const sa = pkg?.shipping_address || {};
  const initialRegionName = sa.city?.name || "";
  const initialCommuneName = sa.municipality?.name || "";
  const initialStreetName = sa.street_name || sa.address_line || "";
  const initialStreetNumber = sa.street_number || "";

  const [catalog, setCatalog] = useState({ status: "loading", regions: [], coverage: null });
  // `regionCode` null = región del pedido que el catálogo no reconoce (o todavía
  // no cargó). `regionName` es la que se muestra.
  const [regionCode, setRegionCode] = useState(null);
  const [regionName, setRegionName] = useState(initialRegionName);
  const [commune, setCommune] = useState(initialCommuneName);
  // Región y comuna tal como quedaron al abrir (ya con la grafía del catálogo):
  // la opción a la que se puede volver y la referencia de "región sin tocar".
  const [initialSelection, setInitialSelection] = useState({
    regionCode: null,
    regionName: initialRegionName,
    commune: initialCommuneName,
  });
  const [streetName, setStreetName] = useState(initialStreetName);
  const [streetNumber, setStreetNumber] = useState(initialStreetNumber);
  const [comment, setComment] = useState(sa.comment || "");
  const [zipCode, setZipCode] = useState(sa.zip_code || "");
  const [point, setPoint] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Catálogo y cobertura juntos: mostrar el catálogo entero y achicarlo después
  // cambiaría la lista con el usuario ya eligiendo.
  useEffect(() => {
    if (countryLoading || !countryId) return undefined;
    let alive = true;
    Promise.all([getRegions({ countryId }), resolveCoverage(countryId)])
      .then(([regions, coverageResult]) => {
        if (!alive) return;
        // getRegions no lanza: ante un fallo devuelve `{ error }`.
        if (!Array.isArray(regions)) {
          setCatalog({ status: "error", regions: [], coverage: null });
          return;
        }
        setCatalog({ status: "ready", regions, coverage: coverageResult?.coverage ?? null });
        const match = findCatalogRegion(regions, initialRegionName, initialCommuneName);
        if (!match) return;
        // El formulario muestra la grafía del catálogo. La comuna viaja así (el
        // backend la compara normalizada); la región no: ver `sentRegionName`.
        const communeKey = normalizeCommune(initialCommuneName);
        const catalogCommune = (match.communes ?? []).find((c) => normalizeCommune(c?.name) === communeKey);
        const selection = {
          regionCode: match.code,
          regionName: match.name,
          commune: catalogCommune?.name ?? initialCommuneName,
        };
        setInitialSelection(selection);
        setRegionCode(selection.regionCode);
        setRegionName(selection.regionName);
        setCommune(selection.commune);
      })
      .catch((err) => {
        console.error("[EditDeliveryAddressModal] No se pudo cargar el catálogo de regiones", err);
        if (alive) setCatalog({ status: "error", regions: [], coverage: null });
      });
    return () => {
      alive = false;
    };
  }, [countryId, countryLoading, initialRegionName, initialCommuneName]);

  // La región de la apertura sigue en la lista aunque se elija otra, para poder
  // volver a ella (aunque la cobertura o el catálogo la dejen fuera).
  const regionOptions = useMemo(() => {
    const options = filterRegionsByCoverage(
      catalog.regions.map((r) => ({ value: r.code, label: r.name })),
      catalog.coverage,
    );
    const option = (code, name) => (name ? { value: code ?? CURRENT_REGION_VALUE, label: name } : null);
    return withOption(
      withOption(options, option(initialSelection.regionCode, initialSelection.regionName)),
      option(regionCode, regionName),
    );
  }, [catalog, initialSelection, regionCode, regionName]);

  const communeOptions = useMemo(() => {
    const region = regionCode ? catalog.regions.find((r) => r.code === regionCode) : null;
    const options = filterCommunesByCoverage(
      (region?.communes ?? []).map((c) => ({ value: c.name, label: c.name })),
      regionCode,
      catalog.coverage,
    );
    return withOption(options, commune ? { value: commune, label: commune } : null);
  }, [catalog, regionCode, commune]);

  // Un punto confirmado para otra calle o comuna mandaría al repartidor al lugar
  // equivocado como si fuera verificado: cambiar la dirección lo descarta.
  const changeAddress = (setter) => (value) => {
    setter(value);
    setPoint(null);
  };

  // Volver a la región de la apertura restaura también su comuna: la región queda
  // "sin tocar" y no hay que volver a elegir nada.
  const handleRegionChange = (value) => {
    if (value === (initialSelection.regionCode ?? CURRENT_REGION_VALUE)) {
      setRegionCode(initialSelection.regionCode);
      setRegionName(initialSelection.regionName);
      setCommune(initialSelection.commune);
    } else {
      const region = catalog.regions.find((r) => r.code === value);
      setRegionCode(region?.code ?? null);
      setRegionName(region?.name ?? "");
      setCommune("");
    }
    setPoint(null);
  };

  const regionTouched =
    regionCode !== initialSelection.regionCode || regionName !== initialSelection.regionName;
  // Sin tocar la región se envía el nombre guardado y no el del catálogo: son
  // nombres distintos ("Región Metropolitana" vs "Región Metropolitana de
  // Santiago"), no grafías, y el backend daría por cambiada una dirección que
  // nadie tocó.
  const sentRegionName = regionTouched || !initialRegionName ? regionName : initialRegionName;

  // La coordenada guardada solo vale como punto de partida para la dirección de
  // la apertura: con otra calle, "Usar este punto" confirmaría como verificada
  // la ubicación vieja.
  const addressUnchanged =
    streetName.trim() === initialStreetName.trim() &&
    streetNumber.trim() === initialStreetNumber.trim() &&
    normalizeCommune(commune) === normalizeCommune(initialCommuneName);
  const pickerQuery = `${[streetName.trim(), streetNumber.trim()].filter(Boolean).join(" ")}${
    commune ? `, ${commune}` : ""
  }`;
  const pickerInitialPoint = point || (addressUnchanged ? toPoint(sa.latitude, sa.longitude) : null);

  const validate = () => {
    const next = {};
    const tooLong = (value, max) =>
      value.trim().length > max ? t("Máximo {{max}} caracteres", { max }) : null;
    if (!regionName) next.region = t("Este campo es obligatorio");
    if (!commune) next.commune = t("Este campo es obligatorio");
    if (!streetName.trim()) next.streetName = t("Este campo es obligatorio");
    // `maxLength` frena lo que se escribe, no un valor precargado más largo.
    const lengthErrors = {
      streetName: next.streetName ? null : tooLong(streetName, STREET_NAME_MAX),
      streetNumber: tooLong(streetNumber, STREET_NUMBER_MAX),
      comment: tooLong(comment, COMMENT_MAX),
      zipCode: postalEnabled ? tooLong(zipCode, ZIP_CODE_MAX) : null,
    };
    Object.entries(lengthErrors).forEach(([field, message]) => {
      if (message) next[field] = message;
    });
    const zip = zipCode.trim();
    if (postalEnabled && zip && !next.zipCode && !isValidPostalCode(zip)) {
      next.zipCode = t("Usa un CPA (M5500ABC) o un código de 4 dígitos (5500)");
    }
    return next;
  };

  // Sin el campo de código postal (países que no lo usan) se reenvía el que trajo
  // el pedido (MercadoLibre, Shopify…): el backend borra los opcionales ausentes.
  const zipCodeField = postalEnabled
    ? optionalField("zip_code", zipCode.trim().toUpperCase(), sa.zip_code)
    : sa.zip_code
      ? { zip_code: sa.zip_code }
      : {};

  const buildPayload = () => ({
    street_name: streetName.trim(),
    ...optionalField("street_number", streetNumber.trim(), sa.street_number),
    ...optionalField("comment", comment.trim(), sa.comment),
    ...zipCodeField,
    municipality: { name: commune },
    city: { name: sentRegionName },
    ...(point && { location: { latitude: point.lat, longitude: point.lng } }),
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSaving(true);
    const res = await updateDeliveryAddress(pkg._id, buildPayload());
    setSaving(false);

    if (res.success) {
      if (res.data?.changed === false) {
        showToast(t("No hubo cambios: la dirección es la misma que ya tenía el pedido."), "info");
        onClose();
        return;
      }
      showToast(t("Dirección de entrega actualizada."), "success");
      showToast(t("Si ya imprimiste la etiqueta, reimprímela: tiene la dirección anterior."), "warning");
      onSaved(res.data?.package);
      onClose();
      return;
    }

    const { status, data } = res.error || {};
    if (status === 409 && data?.code === "ADDRESS_EDIT_LOCKED") {
      showToast(
        data.reason === "in_route"
          ? t("El pedido ya está en manos del repartidor: solo él puede ajustar la dirección.")
          : t("El pedido ya no admite cambios de dirección."),
        "warning",
      );
      onLocked();
      onClose();
      return;
    }
    showToast(t("No se pudo guardar la dirección. Intenta nuevamente."), "error");
  };

  const catalogLoading = catalog.status === "loading";
  // Con el mapa abierto la dirección queda fija: el punto que se va a confirmar
  // es el de esa dirección.
  const addressLocked = saving || pickerOpen;
  const fieldId = (name) => `${formId}-${name}`;

  return (
    <Modal
      open
      onClose={() => (saving ? null : onClose())}
      title={t("Editar dirección de entrega")}
      description={t("Pedido #{{number}}", { number: pkg?.followingNumber })}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t("Cancelar")}
          </Button>
          <Button
            type="submit"
            form={formId}
            loading={saving}
            disabled={catalogLoading || pickerOpen}
            title={pickerOpen ? t("Confirma o cancela el punto en el mapa antes de guardar") : undefined}
          >
            {t("Guardar dirección")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* El buscador del mapa queda FUERA del formulario: un Enter ahí no debe guardar. */}
        <form id={formId} onSubmit={handleSubmit} noValidate className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <FieldLabel htmlFor={fieldId("region")}>{`${regionWord} *`}</FieldLabel>
            <SearchableSelect
              id={fieldId("region")}
              options={regionOptions}
              value={regionCode ?? (regionName ? CURRENT_REGION_VALUE : "")}
              onChange={handleRegionChange}
              placeholder={catalogLoading ? t("Cargando...") : t("Selecciona región")}
              isDisabled={catalogLoading || addressLocked}
              error={errors.region}
            />
          </div>
          <div className="flex flex-col gap-1">
            <FieldLabel htmlFor={fieldId("commune")}>{`${communeWord} *`}</FieldLabel>
            <SearchableSelect
              id={fieldId("commune")}
              options={communeOptions}
              value={commune}
              onChange={changeAddress(setCommune)}
              placeholder={regionName ? t("Selecciona comuna") : t("Selecciona región primero")}
              isDisabled={catalogLoading || addressLocked || !regionName}
              error={errors.commune}
            />
          </div>
          {catalog.status === "error" && (
            <p className="text-xs text-status-error sm:col-span-2">
              {t("No se pudo cargar el catálogo de regiones: solo puedes conservar la región y la comuna actuales.")}
            </p>
          )}

          <div className="flex flex-col gap-1">
            <FieldLabel htmlFor={fieldId("street")}>{t("Calle *")}</FieldLabel>
            <Input
              id={fieldId("street")}
              value={streetName}
              onChange={(e) => changeAddress(setStreetName)(e.target.value)}
              maxLength={STREET_NAME_MAX}
              invalid={!!errors.streetName}
              disabled={addressLocked}
              autoComplete="off"
            />
            {errors.streetName && <span className="text-xs text-status-error">{errors.streetName}</span>}
          </div>
          <div className="flex flex-col gap-1">
            <FieldLabel htmlFor={fieldId("number")}>{t("Número")}</FieldLabel>
            <Input
              id={fieldId("number")}
              value={streetNumber}
              onChange={(e) => changeAddress(setStreetNumber)(e.target.value)}
              maxLength={STREET_NUMBER_MAX}
              invalid={!!errors.streetNumber}
              disabled={addressLocked}
              autoComplete="off"
            />
            {errors.streetNumber && <span className="text-xs text-status-error">{errors.streetNumber}</span>}
          </div>

          {postalEnabled && (
            <div className="flex flex-col gap-1">
              <FieldLabel htmlFor={fieldId("zip")}>{t("Código postal")}</FieldLabel>
              <Input
                id={fieldId("zip")}
                value={zipCode}
                // Se guarda crudo (trim + mayúsculas), igual que el alta: la
                // derivación CPA → 4 dígitos la hace el backend al tarifar.
                onChange={(e) => setZipCode(e.target.value.toUpperCase())}
                maxLength={ZIP_CODE_MAX}
                placeholder={t("Ej: 5500 o M5500ABC")}
                invalid={!!errors.zipCode}
                disabled={saving}
                autoComplete="off"
              />
              {errors.zipCode && <span className="text-xs text-status-error">{errors.zipCode}</span>}
            </div>
          )}

          <div className="flex flex-col gap-1 sm:col-span-2">
            <FieldLabel htmlFor={fieldId("comment")}>{t("Referencia o instrucciones")}</FieldLabel>
            <Textarea
              id={fieldId("comment")}
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={COMMENT_MAX}
              placeholder={t("Ej: depto 3B, timbre roto, dejar en conserjería")}
              invalid={!!errors.comment}
              disabled={saving}
            />
            {errors.comment && <span className="text-xs text-status-error">{errors.comment}</span>}
          </div>
        </form>

        <section className="flex flex-col gap-2 border-t border-border pt-4" aria-label={t("Punto en el mapa")}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-semibold text-fg">{t("Punto en el mapa (opcional)")}</span>
            {point && !pickerOpen && (
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => setPickerOpen(true)} disabled={saving}>
                  {t("Cambiar punto")}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setPoint(null)} disabled={saving}>
                  {t("Quitar punto")}
                </Button>
              </div>
            )}
          </div>

          {pickerOpen ? (
            <AddressPicker
              instanceId={`edit-address-${pkg?._id}`}
              initialQuery={pickerQuery}
              initialPoint={pickerInitialPoint}
              countryCode={geoDefaults.countryCode}
              focusCenter={geoDefaults.center}
              confirmLabel={t("Usar este punto")}
              onConfirm={({ lat, lng }) => {
                setPoint({ lat, lng });
                setPickerOpen(false);
              }}
              onCancel={() => setPickerOpen(false)}
            />
          ) : point ? (
            <p className="flex items-center gap-1.5 text-sm text-fg">
              <CheckCircle2 size={15} aria-hidden="true" className="text-status-success" />
              {t("Punto confirmado: el repartidor irá exactamente a esa ubicación.")}
            </p>
          ) : (
            <>
              <p className="text-xs text-fg-muted">
                {t("Si no confirmas el punto, la ubicación se calcula automáticamente a partir de la dirección.")}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="self-start"
                iconLeft={<MapPin size={15} aria-hidden="true" />}
                onClick={() => setPickerOpen(true)}
                disabled={saving}
              >
                {t("Confirmar punto en el mapa")}
              </Button>
            </>
          )}
        </section>
      </div>
    </Modal>
  );
}
